import { cast, Instance, SnapshotOut, types, clone } from 'mobx-state-tree'
import { extendLogger, LoggerContexts } from '../../utils/logger'
import {
  ChapterModel,
  ChapterModelType,
  LessonChapterProgressModel,
  LessonChapterProgressModelType
} from '../api-store'

import { withRootStore } from '../extensions'

const logger = extendLogger(LoggerContexts.LessonsStore)

/**
 * Lessons Store Model
 */
export const LessonsStore = types
  .model('LessonsStore')
  .props({
    lessons: types.optional(types.array(LessonChapterProgressModel), []),
    lessonSelected: types.maybe(LessonChapterProgressModel),
    currentChapter: types.maybe(ChapterModel),
    isUserSubscriber: types.maybe(types.boolean)
  })
  .extend(withRootStore)
  .actions((self) => ({
    setUserSubscriber(subscriber: boolean) {
      self.isUserSubscriber = subscriber
    },
    setLessons(data: LessonChapterProgressModelType[]) {
      self.lessons = cast(data)
    },
    setCurrentChapter(data: ChapterModelType) {
      if (data != null) {
        if (data.completed) data.currentPosition = 0

        self.currentChapter = clone(data)
      }
    },
    setSelectedLesson(lessonId: string) {
      const lesson = self.lessons.find((lesson) => lesson.lessonId === lessonId)
      self.lessonSelected = clone(lesson)
    },
    clearLessons() {
      self.lessons.clear()
    },
    setDurationChapter(chapterId: string, duration: number) {
      const chapter = self.lessonSelected?.chapters?.find((ch) => ch._id === chapterId)
      if (!chapter) {
        return
      }

      chapter.duration = duration
    },
    setProgressChapter(chapterId: string, currentPosition?: number, completed?: boolean) {
      const chapter = self.lessonSelected?.chapters?.find((ch) => ch._id === chapterId)
      if (!chapter) {
        return
      }

      if (currentPosition) chapter.currentPosition = currentPosition
      if (typeof completed === 'boolean') chapter.completed = completed
    },
    updateChapterCompletion(lessonId: string, chapterId: string, completed: boolean) {
      const lesson = self.lessons.find((lesson) => lesson.lessonId === lessonId)
      if (!lesson) {
        return
      }

      const chapter = lesson.chapters?.find((ch) => ch._id === chapterId)
      if (!chapter) {
        return
      }

      chapter.completed = completed
    }
  }))
  .views((self) => ({
    getLessonById(lessonId: string) {
      return self.lessons.find((lesson) => lesson.lessonId === lessonId)
    }
  }))
  .actions((self) => ({
    async fetchUserChaptersAndProgress() {
      try {
        logger.debug('Fetching user chapters and progress')
        const response = await self.rootStore.api.queryGetUserChaptersAndProgress(
          undefined,
          `
            lessonId
            name
            skills
            teacher {
              name
              image
              role
              statement
            }
            duration
            image
            chapters {
              _id
              name
              order
              membership
              visibility
              completed
              currentPosition
              videoUrl
              subtitlesUrl
              duration
            }
          `,
          {
            fetchPolicy: 'no-cache'
          }
        )
        if (response?.getUserChaptersAndProgress) {
          self.setLessons(response.getUserChaptersAndProgress)
          logger.debug('User chapters and progress fetched successfully')
        }
      } catch (error) {
        logger.error('Error fetching user chapters and progress', error)
        throw error
      }
    },
    async updateUserChapterProgress(
      progressData: {
        completed: boolean
        currentPosition: number
        visibility: boolean
      },
      lessonId: string,
      chapterId: string
    ) {
      try {
        const response = await self.rootStore.api.mutateUpdateUserChapterProgress(
          {
            progressData,
            lessonId,
            chapterId
          },
          `
            chapterId
            completed
          `
        )

        if (response?.updateUserChapterProgress) {
          self.updateChapterCompletion(lessonId, chapterId, progressData.completed)
        }
      } catch (error) {
        logger.error('Error updating user chapter progress', error)
        throw error
      }
    }
  }))

/**
 * The LessonsStore instance.
 */
export type LessonsStoreType = Instance<typeof LessonsStore>

/**
 * The data of a LessonsStore.
 */
export type LessonsStoreSnapshot = SnapshotOut<typeof LessonsStore>
